import { Nullable } from "..";

export type IRecommendationType = "advice" | "co-invest" | "sell" | "connect" | "upgrade-freemium-account";

export interface IRecommendationDataMessage {
  message: string;
  user_id?: number;
  type: IRecommendationType;
}

export interface IAssetAllocation {
  id: number;
  title: string;
  splittable: boolean;
}

export interface IIndustryExpertise {
  id: number;
  title: string;
}

export interface IHobby {
  id: number;
  title: string;
}

export interface ISocialAssetClass {
  id: number;
  title: string;
}

export enum InterestType {
  SCHOOL = "school",
  HEART = "cardiology",
  VOLUNTEER = "volunteer_activism",
  YARD = "yard",
  ENERGY = "energy_savings_leaf",
  DIVERSITY2 = "diversity_2",
  PALETTE = "palette",
  FEMALE = "female",
  CHILD = "child_care",
  HANDSHAKE = "handshake",
  SUPPORT = "support",
  CRUELTY = "cruelty_free",
  BIOTECH = "biotech",
  DIVERSITY1 = "diversity_1",
  AGRICULTURE = "agriculture",
  PAYMENTS = "payments",
  LOCATION = "location_away",
  ELDERLY = "elderly",
  TSUNAMI = "tsunami",
  PSYCHOLOGY = "psychology",
  BALANCE = "balance",
  VACCINES = "vaccines",
  PUBLIC = "public",
  MILITARY = "military_tech",
  RESTAURANT = "restaurant",
  TROPHY = "trophy",
  MEDICAL = "medical_services",
  CONNECT = "connect_without_contact",
  MEMORY = "memory",
  SATELLITE = "satellite_alt",
  OUTPATIENT = "outpatient_med",
  MODEL = "model_training",
  BRUSH = "brush",
  DIVERSITY4 = "diversity_4",
  ECO = "temp_preferences_eco",
  FLAG = "emoji_flags"
}

export interface IInterests {
  id: number;
  title: string;
  icon: InterestType;
}

export interface ISocialAssetClassesMeta extends ISocialAssetClass {
  group: IProfileAssetClassGroup;
}

export interface IProfileMetadata {
  asset_allocations: IAssetAllocation[];
  industry_expertises: IIndustryExpertise[];
  hobbies: IHobby[];
  asset_classes: ISocialAssetClassesMeta[];
  interests: IInterests[];
}

export interface IProfileUser {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  position: Nullable<string>;
  location: Nullable<string>;
  about: Nullable<string>;
  company_name: string;
  profile_photo_url: string;
  show_social_profile: boolean;
}

export interface IProfileIndustryData extends IIndustryExpertise {
  operational: boolean;
  investment: boolean;
  learn_more: boolean;
}

export type IProfileAssetClassGroup = "Commodities" | "Private Equity";

export interface IProfileAssetClass extends ISocialAssetClass {
  group: IProfileAssetClassGroup;
  investment: boolean;
  learn_more: boolean;
}

export type IProfileGroupedAssetClasses = Record<IProfileAssetClassGroup, IProfileAssetClass[]>;

export interface IProfileInvestment {
  id: number;
  company_name: string;
  company_logo: string;
}

export interface IProfileAssetAllocation {
  id: Nullable<number>; //nullable in case of not filled data, API return it with percentage but without id
  title: string;
  percentage: number;
  direct: number;
  funds: number;
  splittable: boolean;
}

export interface IProfileKpi {
  label: string;
  value: number;
  type: "measure" | "count";
  as_of_date: string | null;
  postfix: string;
}

export interface IProfileStats {
  deals_count: number;
  kpis: IProfileKpi[];
}

export interface IProfileData {
  details: IProfileUser;
  industry_expertises: IProfileIndustryData[];
  hobbies: IHobby[];
  asset_classes: IProfileGroupedAssetClasses;
  interests: IInterests[];
  investments: IProfileInvestment[];
  asset_allocations: IProfileAssetAllocation[];
}

// export type IUpdateProfileUser = Omit<IProfileUser, "full_name">;
export interface IUpdateProfileUser extends IProfileUser {
  photo?: File;
}

export type IUpdateProfileIndustry = Omit<IProfileIndustryData, "title">;

export type IUpdateProfileAssetClasses = Omit<IProfileAssetClass, "title" | "group">;

export type IUpdateProfileAssetAllocation = Omit<IProfileAssetAllocation, "title">;

export interface IUpdateProfileData {
  userId: number;
  details?: IUpdateProfileUser;
  industry_expertises?: IUpdateProfileIndustry[];
  hobbies?: number[];
  interests?: number[];
  investments?: string[];
  asset_classes?: IUpdateProfileAssetClasses[];
  asset_allocations?: IUpdateProfileAssetAllocation[];
}
